/**
 * What an entity's detail shows of its properties and relationships under the "show no longer
 * valid" switch (PRD-0075 R-C4): without what no longer holds or is wrong, or with it and the
 * relationships to grey. `hasHidden` says whether the entity has anything the switch would bring back.
 */
import { useMemo } from 'react'
import { isHiddenFact, propertyInvalidations, withoutInvalidProperties } from './fact-marks'
import { useShowNoLongerValid } from './useFactVisibility'

interface Relationship {
  id: string
  properties?: Record<string, unknown>
}

const NO_PROPERTIES: Record<string, unknown> = {}

export function useEntityFactVisibility<R extends Relationship>(
  properties: Record<string, unknown> | undefined,
  relationships: readonly R[]
) {
  const [show] = useShowNoLongerValid()
  return useMemo(() => {
    const all = properties ?? NO_PROPERTIES
    const hiddenRelationships = new Set(
      relationships.filter(r => isHiddenFact(r.properties)).map(r => r.id)
    )
    return {
      properties: show ? all : withoutInvalidProperties(all),
      relationships: show
        ? [...relationships]
        : relationships.filter(r => !hiddenRelationships.has(r.id)),
      fadedRelationshipIds: show ? hiddenRelationships : new Set<string>(),
      hasHidden: hiddenRelationships.size > 0 || propertyInvalidations(all).size > 0,
    }
  }, [properties, relationships, show])
}
